import React, { useEffect, useState, useRef, useMemo } from "react";
import {
    Box,
    Typography,
    Avatar,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import Axios from "../../../config/axios";
import { UrlConstant } from "../../../commom/UrlConstant";
import { endLoading, startLoading } from "../../../Redux/loadingSlice";
import { AccessDeniedUI } from "../SharedComponent/AccessDeniedUI";
import { usePriceFormatter } from "../../../hooks";
import { formatPercentage } from "../../../utils/formatting";
import { useAppDispatch } from "../../../Redux/hooks";

interface RawCryptoItem {
    type: "crypto";
    _id: string;
    cmc_id: number;
    name: string;
    symbol: string;
    logo: string;
    isLoanAvailable: boolean;
    formatted_symbol?: string;
    currency_pair?: string;
    quote?: {
        USD?: {
            price?: number;
            percent_change_24h?: number;
            volume_24h?: number;
        };
    };
}

interface RawStockItem {
    type: "stock";
    _id: string;
    symbol: string;
    displayName: string;
    stockPrice?: number;
    regularMarketPrice?: string | number;
    Last?: number;
    changeAmount?: number;
    changePercentage?: number;
    isLoanAvailable: boolean;
    category?: string | null;
    quoteType?: string;
    annualReportExpenseRatioFmt?: string | null;
    branding?: {
        logo_url?: string;
        icon_url?: string;
    };
}

type RawItem = RawCryptoItem | RawStockItem;

interface WatchlistItem {
    _id: string;
    type: "crypto" | "stock" | "etf";
    icon: string;
    name: string;
    symbol: string;
    currencyPair?: string;
    price: number;
    changeAmount: number;
    changePercentage: number;
    externalID?: number;
    cmcId?: number;
    isLoanAvailable: boolean;
}

interface WatchlistPanelProps {
    userId: string;
}

function isEtf(s: RawStockItem): boolean {
    return (
        s.quoteType === "ETF" ||
        (s.annualReportExpenseRatioFmt != null && s.annualReportExpenseRatioFmt !== "null") ||
        (s.category != null && s.category !== "null")
    );
}

function formatCurrencyPair(raw?: string): string {
    if (!raw) return "";
    if (raw.includes("/")) return raw.toUpperCase();
    const quotes = ["usdt", "usd", "btc", "eth", "bnb", "usdc"];
    const lower = raw.toLowerCase();
    for (const q of quotes) {
        if (lower.endsWith(q)) {
            const base = lower.slice(0, lower.length - q.length).toUpperCase();
            return `${base}/${q.toUpperCase()}`;
        }
    }
    return raw.toUpperCase();
}

const BADGE_CONFIG = {
    crypto: { label: "Crypto", bg: "#F2674A", color: "#000000", border: "#F2674A" },
    stock: { label: "Stock", bg: "#DFDD4E", color: "#000000", border: "#DFDD4E" },
    etf: { label: "ETF", bg: "#4EDF6D", color: "#000000", border: "#4EDF6D" },
};

const customStyles = {
    headCells: {
        style: {
            fontSize: "14px",
            width: "100% !important",
            justifyContent: "left",
            whiteSpace: "wrap",
            overflow: "hidden",
            color: "white",
            borderBottom: "1px solid #ffffff33 !important",
            fontWeight: "600",
            backgroundColor: "transparent",
            paddingBottom: "12px",
            paddingTop: "16px",
        },
    },
    cells: {
        style: {
            justifyContent: "left",
            whiteSpace: "normal",
            textOverflow: "ellipsis",
            color: "white",
            fontSize: "16px",
            border: "none",
            cursor: "pointer",
            padding: "12px 8px",
            transition: "all 0.1s ease-in-out",
            backgroundColor: "transparent",
        },
    },
    rows: {
        style: {
            borderColor: "transparent !important",
            backgroundColor: "transparent",
            width: "96% !important",
            borderRadius: "8px",
            margin: "2px 8px",
            padding: "4px",
            transition: "all 0.3s ease",
            cursor: "pointer",
            outline: "none !important",
            "&:hover": {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: "#4A7AF21A !important",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(74, 122, 242, 0.15) !important",
                outline: "none !important",
            },
            "&:nth-child(even)": {
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
            },
            "&:nth-child(odd)": {
                backgroundColor: "transparent",
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
            },
            "&:nth-child(even):hover": {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: "#4A7AF21A !important",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(74, 122, 242, 0.15) !important",
            },
            "&:nth-child(odd):hover": {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: "#4A7AF21A !important",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(74, 122, 242, 0.15) !important",
            },
        },
    },
    headRow: {
        style: {
            backgroundColor: "transparent",
        },
    },
};

function MyWatchlist({ userId }: WatchlistPanelProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const tableRef = useRef<HTMLDivElement>(null);

    const [rows, setRows] = useState<WatchlistItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [isAccessDenied, setIsAccessDenied] = useState(false);
    const formatter = usePriceFormatter();

    const columns = useMemo(() => [
        {
            name: "Asset",
            cell: (row: WatchlistItem) => (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {row.icon && (
                        <img
                            src={row.icon}
                            alt={row.name}
                            style={{ width: 32, height: 32, borderRadius: "50%" }}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                            }}
                        />
                    )}
                    <div>
                        <div style={{ fontWeight: 600, color: "white" }}>
                            {row.name}
                        </div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                            {row.type === "crypto"
                                ? row.currencyPair || row.symbol
                                : row.symbol}
                        </div>
                    </div>
                </div>
            ),
            grow: 2,
        },

        {
            name: "Type",
            width: "110px",
            cell: (row: WatchlistItem) => {
                const badge = BADGE_CONFIG[row.type];
                return (
                    <span
                        style={{
                            padding: "4px 12px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                            background: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                        }}
                    >
                        {badge.label}
                    </span>
                );
            },
            grow: 1,
        },

        {
            name: "Price",
            cell: (row: WatchlistItem) => (
                <span style={{ color: "white", fontWeight: 600 }}>
                    {formatter.price(row.price).display}
                </span>
            ),
            grow: 1,
        },

        {
            name: "24h Change",
            cell: (row: WatchlistItem) => {
                const isPositive = row.changeAmount >= 0;
                const color = isPositive ? "#4edf6d" : "#ff2a2a";

                const changeAmount = formatter.display(Math.abs(row.changeAmount), { showSign: true });
                const changePercent = formatPercentage(row.changePercentage, { showSign: true });

                return (
                    <span style={{ color }}>
                        {changeAmount}{" "}
                        <span style={{ opacity: 0.8 }}>
                            ({changePercent})
                        </span>
                    </span>
                );
            },
            grow: 1,
        },
    ], [formatter]);



    const fetchWatchlist = async (pageNum: number, limitNum: number) => {
        if (!userId) return;
        dispatch(startLoading());
        setLoading(true);
        try {
            const offset = (pageNum - 1) * limitNum;
            const url =
                UrlConstant.Profile.userAssetData.replace("{0}", userId) +
                `?type=watchlist&limit=${limitNum}&offset=${offset}`;

            const response = await Axios.get(url);
            const resData = response.data;

            if (resData.status === true) {
                const list: WatchlistItem[] = (resData.data as RawItem[]).map((item) => {
                    if (item.type === "crypto") {
                        const c = item as RawCryptoItem;
                        const usd = c.quote?.USD;
                        const price = usd?.price ?? 0;
                        const changePercentage = usd?.percent_change_24h ?? 0;
                        const changeAmount = price * (changePercentage / 100);
                        return {
                            _id: c._id,
                            type: "crypto" as const,
                            icon: c.logo,
                            name: c.name,
                            symbol: c.symbol,
                            currencyPair: formatCurrencyPair(c.currency_pair ?? c.formatted_symbol),
                            price,
                            changeAmount,
                            changePercentage,
                            externalID: c.cmc_id,
                            cmcId: c.cmc_id,
                            isLoanAvailable: c.isLoanAvailable,
                        };
                    } else {
                        const s = item as RawStockItem;
                        const price =
                            s.Last ??
                            (typeof s.stockPrice === "number" ? s.stockPrice : undefined) ??
                            (typeof s.regularMarketPrice === "number"
                                ? s.regularMarketPrice
                                : parseFloat(s.regularMarketPrice as string) || 0);
                        return {
                            _id: s._id,
                            type: (isEtf(s) ? "etf" : "stock") as "etf" | "stock",
                            icon: s.branding?.icon_url ?? s.branding?.logo_url ?? "",
                            name: s.displayName,
                            symbol: s.symbol,
                            price,
                            changeAmount: s.changeAmount ?? 0,
                            changePercentage: s.changePercentage ?? 0,
                            isLoanAvailable: s.isLoanAvailable,
                        };
                    }
                });

                setRows(list);
                setTotalRows(resData.pagination?.total ?? list.length);
            }
        } catch (err: any) {
            if (
                err?.response?.data?.status === "fail" &&
                err?.response?.data?.message?.includes("You are not allowed to view this profile")
            ) {
                setIsAccessDenied(true);
            } else {
                console.error("Failed to fetch watchlist:", err);
            }
        } finally {
            setLoading(false);
            dispatch(endLoading());
        }
    };

    useEffect(() => {
        fetchWatchlist(page, rowsPerPage);
    }, [userId, page, rowsPerPage]);

    // Row click navigation (same pattern as MyTrades)
    useEffect(() => {
        const addClickHandlers = () => {
            if (tableRef.current && rows.length > 0) {
                tableRef.current.addEventListener("click", (event) => {
                    const target = event.target as HTMLElement;
                    const isButton = target.closest(
                        "button, a, [role=\"button\"], input, select, textarea, .MuiButton-root, .MuiIconButton-root, svg, path"
                    );
                    if (isButton) return;

                    const potentialRowElements = [
                        target.closest("[class*=\"rdt_TableRow\"]"),
                        target.closest("[id^=\"row-\"]"),
                        target.closest("[class*=\"rdt_TableBodyRow\"]"),
                        target.closest("[role=\"row\"]"),
                        target.closest("[data-tid*=\"row\"]"),
                    ].filter((el): el is Element => el !== null);

                    const actualRow =
                        potentialRowElements.find((el) => {
                            const className = el.className?.toString?.() || "";
                            return className.includes("rdt_TableRow");
                        }) || potentialRowElements[0];

                    if (actualRow) {
                        if (!tableRef.current) return;
                        const allTableRows = Array.from(
                            tableRef.current.querySelectorAll("[class*=\"rdt_TableRow\"]")
                        );
                        const rowIndex = allTableRows.indexOf(actualRow);
                        if (rowIndex >= 0 && rowIndex < rows.length && rows[rowIndex]) {
                            handleCardClick(rows[rowIndex]);
                        }
                    }
                });
            }
        };

        const timer = setTimeout(addClickHandlers, 500);
        return () => clearTimeout(timer);
    }, [rows]);

    const handleCardClick = (item: WatchlistItem) => {
        if (item.type === "crypto") {
            navigate("/crypto-detail", { state: { externalID: item.externalID } });
        } else {
            navigate("/stockdetail", {
                state: { symbol: item.symbol, title: "Watchlist", icon: item.icon },
            });
        }
    };

    const handlePageChange = (newPage: number) => setPage(newPage);
    const handleRowsPerPageChange = (newLimit: number) => {
        setRowsPerPage(newLimit);
        setPage(1);
    };

    if (isAccessDenied) return <AccessDeniedUI />;

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 71px - 143px)">
                <CircularProgress style={{ color: "#4A7AF2" }} size={36} />
            </Box>
        );
    }

    if (!loading && rows.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 71px - 143px)">
                <Typography variant="h6" style={{ color: "white" }}>No Watchlist Found</Typography>
            </Box>
        );
    }

    return (
        <Box
            className="custom-table-container"
            sx={{
                border: "1px solid #ffffff33",
                borderRadius: "20px",
                overflow: "visible",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <style>{`
                .rdt_TableWrapper,
                .rdt_Table,
                .rdt_TableHead,
                .rdt_TableHeadRow,
                .rdt_TableBody,
                .rdt_TableRow,
                .rdt_Pagination,
                [class*="sc-"] {
                    background-color: transparent !important;
                }

                div[class*="ProgressWrapper"],
                div[role="progressbar"],
                .rdt_Table > div:first-child {
                    background-color: transparent !important;
                }

                .rdt_TableCol input[type="checkbox"],
                .rdt_TableCell input[type="checkbox"] {
                    appearance: none;
                    -webkit-appearance: none;
                    width: 24px;
                    height: 24px;
                    border-radius: 6px;
                    border: 1.5px solid rgba(255, 255, 255, 0.35);
                    background-color: transparent;
                    cursor: pointer;
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                }
                .rdt_TableCol input[type="checkbox"]:checked,
                .rdt_TableCell input[type="checkbox"]:checked {
                    background-color: #4A7AF2;
                    border-color: #4A7AF2;
                }
                .rdt_TableCol input[type="checkbox"]:checked::after,
                .rdt_TableCell input[type="checkbox"]:checked::after {
                    content: "";
                    position: absolute;
                    width: 9px;
                    height: 5px;
                    border-left: 2px solid #ffffff;
                    border-bottom: 2px solid #ffffff;
                    transform: rotate(-45deg);
                }
                .rdt_TableWrapper, .rdt_Table, .rdt_TableHead, .rdt_TableHeadRow,
                .rdt_TableBody, .rdt_TableRow, .rdt_Pagination {
                    color: white !important;
                }
                .custom-table-container button:disabled {
                    fill: #a3a3a370 !important;
                    color: #a3a3a370 !important;
                }
                .custom-table-container button {
                    fill: #FFFFFF !important;
                    color: #FFFFFF !important;
                }
                .rdt_TableHead {
                    overflow: hidden !important;
                }
            `}</style>

            <div
                ref={tableRef}
                style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
                <DataTable
                    columns={columns}
                    data={rows}
                    style={{ backgroundColor: "transparent" }}
                    fixedHeader={false}
                    onChangePage={handlePageChange}
                    responsive={true}
                    highlightOnHover={true}
                    pointerOnHover={true}
                    dense={false}
                    persistTableHead={false}
                    noHeader={false}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    paginationTotalRows={totalRows}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationDefaultPage={page}
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[5, 10, 20, 50]}
                    progressComponent={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "40px",
                                backgroundColor: "transparent",
                                minHeight: "200px",
                            }}
                        >
                            <CircularProgress size="40px" style={{ color: "#4A7AF2" }} />
                        </div>
                    }
                    noDataComponent={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                padding: "24px",
                                color: "white",
                                fontSize: "16px",
                                backgroundColor: "transparent",
                            }}
                        >
                            There are no records to display
                        </div>
                    }
                />
            </div>
        </Box>
    );
}

export default MyWatchlist;